'use client';

import { useQueryUser } from '@/hooks/query/useQueryUser';
import { isOnPrem } from '@/utils/buildProfile';
import { SessionSettingsRequestConnected_apps_providerAnyOf0 as ConnectedAppsProviderWire } from '@heidi/schema/ml-scribe';
import { useUpdateUserSettings } from '@heidi/workspace/data';
import { useCallback, useEffect, useRef, useState } from 'react';

export type ConnectedAppsProvider = 'nango' | 'merge';

const STORAGE_KEY = 'heidi.connected-apps-provider';

function isProvider(value: unknown): value is ConnectedAppsProvider {
  return value === 'nango' || value === 'merge';
}

function readStoredProvider(): ConnectedAppsProvider | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (isProvider(raw)) return raw;
  } catch {
    // ignore
  }
  return null;
}

function writeStoredProvider(next: ConnectedAppsProvider): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // ignore
  }
}

/**
 * Connected apps stack switcher (Nango vs Merge Agent Handler).
 *
 * Source of truth is ``session_settings.connected_apps_provider`` so the Work
 * agent binds only that stack's tools. localStorage is an optimistic cache and
 * one-time migration source for pre-persistence clients. On-prem is Nango only.
 *
 * When the server value is unset (null), UI defaults to nango and persists that
 * (or migrates a prior localStorage choice) so the agent gets an exclusive stack.
 */
export function useConnectedAppsProvider(): {
  provider: ConnectedAppsProvider;
  setProvider: (next: ConnectedAppsProvider) => void;
} {
  const { data: serverProvider } = useQueryUser(
    (data) => data?.session_settings?.connected_apps_provider
  );
  const { mutate: updateSettings } = useUpdateUserSettings({ showToast: false });
  const [provider, setProviderState] = useState<ConnectedAppsProvider>('nango');
  const migratedRef = useRef(false);

  useEffect(() => {
    if (isOnPrem) {
      setProviderState('nango');
      return;
    }
    if (isProvider(serverProvider)) {
      setProviderState(serverProvider);
      writeStoredProvider(serverProvider);
      return;
    }
    // Server unset: prefer prior localStorage, else default nango — and persist
    // so the agent starts filtering tools.
    const next = readStoredProvider() ?? 'nango';
    setProviderState(next);
    writeStoredProvider(next);
    if (!migratedRef.current) {
      migratedRef.current = true;
      updateSettings({
        session_settings: { connected_apps_provider: ConnectedAppsProviderWire[next] },
      });
    }
  }, [serverProvider, updateSettings]);

  const setProvider = useCallback(
    (next: ConnectedAppsProvider) => {
      if (isOnPrem) return;
      setProviderState(next);
      writeStoredProvider(next);
      updateSettings({
        session_settings: { connected_apps_provider: ConnectedAppsProviderWire[next] },
      });
    },
    [updateSettings]
  );

  return { provider: isOnPrem ? 'nango' : provider, setProvider };
}
